// @ts-strict-ignore
import { Injectable } from '@angular/core';
import momentTimezone from 'moment-timezone';
import { BehaviorSubject, Observable } from 'rxjs';
import { CustomRange } from '@fc-shared/models/custom.range';
import type {
  Moment,
  MomentFormatSpecification,
  MomentInput,
  unitOfTime,
} from 'moment';
import { DateRange } from '@fc-core/models';
import {
  DateTimeFormat,
  resolveDateTimeFormat,
} from '@fc-shared/utils/date-time-formats';

const moment = momentTimezone;

export function setDefaultTimezone(timezone: string) {
  moment.tz.setDefault(timezone);
}

@Injectable({ providedIn: 'root' })
export class MomentService {
  private _timezone: string;

  set timezone(timezone: string) {
    if (!timezone) return;
    this._timezone = timezone;
    this.customRanges$.next(this.fillCustomRanges(moment));
  }

  get timezone(): string {
    return this._timezone;
  }

  private customRanges$: BehaviorSubject<CustomRange[]> = new BehaviorSubject<
    CustomRange[]
  >([]);

  get customRanges(): CustomRange[] {
    return this.customRanges$.getValue();
  }

  get customRangesObs$(): Observable<CustomRange[]> {
    return this.customRanges$.asObservable();
  }

  moment(
    input?: MomentInput,
    format?: MomentFormatSpecification,
    strict?: boolean,
    keepLocalTime?: boolean,
  ): Moment {
    if (this.timezone) {
      moment.tz.setDefault(this.timezone);
      return moment(input, format, strict).tz(this.timezone, keepLocalTime);
    }

    return moment(input, format, strict);
  }

  isBetween(date: Moment, from: Moment, to: Moment): boolean {
    return (
      moment.isMoment(date) &&
      moment.isMoment(from) &&
      moment.isMoment(to) &&
      date.isBetween(from, to)
    );
  }

  fillCustomRanges(moment: typeof momentTimezone): CustomRange[] {
    if (!moment) return;
    return [
      {
        name: 'Today',
        start: moment().startOf('day'),
        end: moment().endOf('day'),
      },
      {
        name: 'Last 7 Days',
        start: moment().subtract(6, 'days').startOf('day'),
        end: moment().endOf('day'),
      },
      {
        name: 'This week',
        start: moment().startOf('week'),
        end: moment().endOf('week'),
      },
      {
        name: 'Last week',
        start: moment().subtract(1, 'week').startOf('week'),
        end: moment().subtract(1, 'week').endOf('week'),
      },
      {
        name: 'This Month',
        start: moment().startOf('month'),
        end: moment().endOf('month'),
      },
      {
        name: 'Last Month',
        start: moment().subtract(1, 'month').startOf('month'),
        end: moment().subtract(1, 'month').endOf('month'),
      },
      {
        name: 'This quarter',
        start: moment().startOf('quarter'),
        end: moment().endOf('quarter'),
      },
      {
        name: 'Last quarter',
        start: moment().subtract(1, 'quarter').startOf('quarter'),
        end: moment().subtract(1, 'quarter').endOf('quarter'),
      },
    ];
  }

  amDuration(time: number, unit: unitOfTime.DurationConstructor): string {
    return moment.duration(time, unit).humanize();
  }

  timeAgo(created: string): string {
    return moment(created).fromNow();
  }

  formatDate(date: Moment, format: DateTimeFormat): string {
    return date.format(resolveDateTimeFormat(format));
  }

  formatDateRange(dateRange: DateRange, format: DateTimeFormat): string {
    if (
      dateRange.start.isSame(moment().startOf('day'), 'day') &&
      dateRange.end.isSame(moment().endOf('day'), 'day')
    ) {
      return 'Today';
    } else if (dateRange.start.isSame(dateRange.end, 'day')) {
      return this.formatDate(dateRange.start, format);
    }
    return `${this.formatDate(dateRange.start, format)} - ${this.formatDate(dateRange.end, format)}`;
  }
}
